import React from "react";
import {
  Typography,
  FormControlLabel,
  Button,
  Box,
  Card,
  CardContent,
  Stack,
  Tooltip,
  IconButton,
  Paper,
} from "@mui/material";
import { Field, FieldArray } from "formik";
import { TextField, Checkbox } from "formik-mui";
import { HelpOutline, Add, Delete } from "@mui/icons-material";
import { patchDesign } from "../DesignActions";
import { updateLoopDataAction } from "../../ProjectActions";

export default function DiscussionChoices({ designs, referenceDesign, payload, dispatch, values, setValues }) {
  const project = payload.project;

  return (
    <Paper sx={{ p: 2, pb: 4, mb: 2  }}>
      <Stack spacing={3}>
        {/* Design choices section (KRA's) */}
        <Box>
          <Typography variant="h6" gutterBottom>
            Kansrijke ontwerpen
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Op basis van de overzichtstabel kan worden besloten welke ontwerpen als kansrijk worden beschouwd. 
            Deze ontwerpen kunnen hier worden geselecteerd. Door een ontwerp te selecteren in deze stap, wordt de onderliggende data van het ontwerp meegenomen naar de volgende ontwerpronde. 
            Per ontwerp dat mee wordt genomen kan een korte toelichting worden gegeven.
          </Typography>

          <Stack spacing={2}>
            {designs.map((design) => {
              if (design.loop === null || design.is_reference) {
                return null;
              } else {
                return (
                  <Card key={design.id} variant="outlined" sx={{ px: 2, pt: 1, pb: 0 }}>
                    <CardContent sx={{ p: 0 }}>
                      <Typography variant="subtitle1">{design.name}</Typography>
                      <FormControlLabel
                        control={
                          <Field
                            disabled={!payload.editable}
                            component={Checkbox}
                            name={`preferred[${design.id}]`}
                            type="checkbox"
                            onChange={(e) => {
                              setValues((prev) => ({
                                ...prev,
                                preferred: { ...prev.preferred, [design.id]: e.target.checked },
                              }));
                              patchDesign(
                                { id: design.id, is_preferred: e.target.checked },
                                payload.phase.id,
                                payload.loop.id,
                                dispatch
                              );
                            }}
                          />
                        }
                        label={
                          <Tooltip title="Vink dit vakje aan om dit ontwerp mee te nemen naar de volgende ontwerpronde">
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <Typography>Kansrijk ontwerp</Typography>
                              <HelpOutline fontSize="small" sx={{ ml: 0.5 }} />
                            </Box>
                          </Tooltip>
                        }
                      />
                      <Field
                        disabled={!payload.editable}
                        fullWidth
                        component={TextField}
                        name={`comments[${design.id}]`}
                        maxRows={10}
                        minRows={2}
                        multiline
                        label="Toelichting"
                        onBlur={(e) => {
                          patchDesign(
                            { id: design.id, comment: e.target.value },
                            payload.phase.id,
                            payload.loop.id,
                            dispatch
                          );
                        }}
                      />
                    </CardContent>
                  </Card>
                );
              }
            })}
          </Stack>
        </Box>

        {/* Conclusions section */}
        <Box>
          <Typography variant="h6">
            Conclusies
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>          
            Om de ontwerpronde af te ronden dient in deze stap te worden gemeld wat de (generieke) conclusies zijn, naast de keuze voor de ontwerpen. 
            Het beschrijft op hoofdlijnen wat in deze ontwerpronde is uitgevoerd, wat de impact is qua duurzaamheid en wat generieke inzichten zijn. 
            De informatie in dit blok kan in een later stadium ook worden gebruikt om te herleiden wat de algemene uitkomst is geweest van de ontwerpronde.
          </Typography>
          <Field
            disabled={!payload.editable}
            fullWidth
            component={TextField}
            name="loopComment"
            maxRows={10}
            minRows={2}
            multiline
            label="Conclusies van deze ontwerpronde"
            onBlur={(e) => {
              updateLoopDataAction(project.id, payload.loop.id, { comment: e.target.value }, dispatch);
            }}
          />
        </Box>
        
        {/* Recommendations section */}
        <Box>
          <Typography variant="h6" gutterBottom>
            Aanbevelingen
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Op basis van de gekozen kansrijke ontwerpen en de conclusies, kunnen aanbevelingen worden geformuleerd voor de vervolgfase. 
            Dit kunnen aanbevelingen zijn om de ontwerpen verder te optimaliseren in het kader van duurzaamheid, maar ook voor de aanpak van het vervolgtraject.
          </Typography>

          <FieldArray name="recommendations">
            {({ push, remove }) => (
              <Stack spacing={2}>
                {values.recommendations && values.recommendations.length > 0 ? (
                  values.recommendations.map((recommendation, index) => (
                    <Box key={index} sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                      <Field
                        disabled={!payload.editable}
                        fullWidth
                        component={TextField}
                        name={`recommendations[${index}]`}
                        label={`Aanbeveling ${index + 1}`}
                        multiline
                        minRows={2}
                        maxRows={10}
                        onBlur={(e) => {
                          updateLoopDataAction(
                            project.id,
                            payload.loop.id,
                            { recommendations: values.recommendations },
                            dispatch
                          );
                          // console.log(values.recommendations);
                        }}
                        autoFocus={index === values.recommendations.length - 1}
                      />
                      {payload.editable && (
                        <IconButton
                          color="error"
                          onClick={() => {
                            updateLoopDataAction(
                              project.id,
                              payload.loop.id,
                              { recommendations: values.recommendations.filter((_, i) => i !== index) },
                              dispatch
                            );
                            remove(index);
                          }}
                          sx={{ mt: 1 }}
                        >
                          <Delete />
                        </IconButton>
                      )}
                    </Box>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Geen aanbevelingen toegevoegd
                  </Typography>
                )}
                {payload.editable && (
                  <Button
                    startIcon={<Add />}
                    onClick={() => push("")}
                    variant="outlined"
                    sx={{ alignSelf: "flex-start" }}
                  >
                    Aanbeveling toevoegen
                  </Button>
                )}
              </Stack>
            )}
          </FieldArray>
        </Box>

        {/* Besluiten section */}
        <Box>
          <Typography variant="h6" gutterBottom>
            Besluiten
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            In dit blok kunnen eventuele besluiten worden opgenomen die ontwerpoverstijgend zijn en meer generiek van aard. 
            Hierbij valt te denken aan uitgangspunten voor de materialisatie en het in te zetten materieel, maar ook werkzaamheden die in de vervolgfase moeten worden uitgevoerd, 
            zoals aanvullend onderzoek of een nadere analyse van ontwerpen. 
            Tot slot kunnen hier ook besluiten qua projectorganisatie worden opgenomen, bijvoorbeeld de overdracht van het werkpakket duurzaamheid binnen het project.
          </Typography>
          <FieldArray name="decisions">
            {({ push, remove }) => (
              <Stack spacing={2}>
                {values.decisions && values.decisions.length > 0 ? (
                  values.decisions.map((decision, index) => (
                    <Box key={index} sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                      <Field
                        disabled={!payload.editable}
                        fullWidth
                        component={TextField}
                        name={`decisions[${index}]`}
                        label={`Besluit ${index + 1}`}
                        multiline
                        minRows={2}
                        maxRows={10}
                        onBlur={(e) => {
                          updateLoopDataAction(
                            project.id,
                            payload.loop.id,
                            { decisions: values.decisions },
                            dispatch
                          );
                          // console.log(values.recommendations);
                        }}
                        autoFocus={index === values.decisions.length - 1}
                      />
                      {payload.editable && (
                        <IconButton
                          color="error"
                          onClick={() => {
                            updateLoopDataAction(
                              project.id,
                              payload.loop.id,
                              { decisions: values.decisions.filter((_, i) => i !== index) },
                              dispatch
                            );
                            remove(index);
                          }}
                          sx={{ mt: 1 }}
                        >
                          <Delete />
                        </IconButton>
                      )}
                    </Box>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Geen besluiten toegevoegd
                  </Typography>
                )}
                {payload.editable && (
                  <Button
                    startIcon={<Add />}
                    onClick={() => push("")}
                    variant="outlined"
                    sx={{ alignSelf: "flex-start" }}
                  >
                    Besluit toevoegen
                  </Button>
                )}
              </Stack>
            )}
          </FieldArray>
        </Box>

      </Stack>
    </Paper>
  );
}
